/**
 * The prism overlay: one SVG laid exactly over the Leaflet container, one
 * isometric column per facility (prismGeometry.ts). Positions come from
 * map.latLngToContainerPoint and are re-projected on moveend, zoomend, resize
 * and viewreset. While the map is being dragged, the whole layer is shifted by
 * the pan offset (one attribute write, no React render), so 700 columns follow
 * the tiles without re-rendering every frame. During a zoom animation the
 * columns are hidden and come back re-projected at the new zoom.
 *
 * Column heights are in screen px from ONE linear scale (k), so zoom never
 * changes them; the scale bar states k.
 *
 * Shading: every face is filled with the category colour, then the right face
 * is darkened and the top lightened with translucent black/white, so any CSS
 * colour (including var(--tide-cat-*)) shades correctly.
 *
 * The SVG is aria-hidden: the ranked list below is the keyboard and
 * screen-reader path to the same facilities and figures. Columns take mouse
 * and touch clicks; the map underneath keeps its own drag/zoom.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type L from 'leaflet';
import { DOMAIN } from '../../../../components/tide/tokens';
import type { AtlasDomain, AtlasFacility } from './atlasApi';
import { ATLAS_DOMAINS } from './atlasApi';
import { PRISM_B, backToFront, diamond, shownTotal, sideFaces, silhouette, stackSegments } from './prismGeometry';

export interface PrismLayerProps {
  map: L.Map | null;
  facilities: AtlasFacility[];
  shown: AtlasDomain[];
  /** px per open record (one scale for every column). */
  k: number;
  selectedId: string | null;
  onSelect(id: string): void;
  /** Grow columns from 0 (false under prefers-reduced-motion). */
  animate: boolean;
  /** Changes when the columns should grow again (first load, legend toggle). */
  growKey: number;
}

interface Placed { f: AtlasFacility; x: number; y: number }

export default function PrismLayer({ map, facilities, shown, k, selectedId, onSelect, animate, growKey }: PrismLayerProps) {
  const [frame, setFrame] = useState<{ placed: Placed[]; w: number; h: number } | null>(null);
  const [zooming, setZooming] = useState(false);
  const rootRef = useRef<SVGGElement | null>(null);
  const anchorRef = useRef<{ x: number; y: number } | null>(null);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;

  useEffect(() => {
    if (!map) return undefined;
    const project = () => {
      const size = map.getSize();
      const placed = backToFront(facilities.map((f) => {
        const p = map.latLngToContainerPoint([f.lat, f.lng]);
        return { f, x: p.x, y: p.y };
      }));
      const a = map.containerPointToLayerPoint([0, 0]);
      anchorRef.current = { x: a.x, y: a.y };
      if (rootRef.current) rootRef.current.removeAttribute('transform');
      setFrame({ placed, w: size.x, h: size.y });
    };
    const drift = () => {
      const a = anchorRef.current;
      if (!a || !rootRef.current) return;
      const now = map.containerPointToLayerPoint([0, 0]);
      rootRef.current.setAttribute('transform', `translate(${a.x - now.x} ${a.y - now.y})`);
    };
    const zoomStart = () => setZooming(true);
    const zoomEnd = () => { setZooming(false); project(); };
    project();
    map.on('moveend resize viewreset', project);
    map.on('move', drift);
    map.on('zoomstart', zoomStart);
    map.on('zoomend', zoomEnd);
    return () => {
      map.off('moveend resize viewreset', project);
      map.off('move', drift);
      map.off('zoomstart', zoomStart);
      map.off('zoomend', zoomEnd);
    };
  }, [map, facilities]);

  // The columns only change with the data, the scale, the shown categories,
  // the selection or a new growth; panning never re-renders them.
  const columns = useMemo(() => {
    if (!frame) return null;
    // Back to front, except the selected column, which is painted last so its ring is never hidden.
    const ordered = selectedId
      ? [...frame.placed.filter((p) => p.f.id !== selectedId), ...frame.placed.filter((p) => p.f.id === selectedId)]
      : frame.placed;
    return ordered.map(({ f, x, y }) => {
      const segs = stackSegments(f.open, shown, ATLAS_DOMAINS, k);
      const height = segs.length ? segs[segs.length - 1].to : 0;
      const top = segs[segs.length - 1];
      const selected = f.id === selectedId;
      const title = `${f.name}: ${ATLAS_DOMAINS.map((d) => `${DOMAIN[d].label} ${f.open[d]}${shown.includes(d) ? '' : ' (hidden)'}`).join(', ')}`;
      return (
        <g
          key={`${f.id}:${growKey}`}
          className="atlas-prism"
          data-testid="prism"
          data-facility={f.id}
          data-selected={selected ? 'true' : undefined}
          data-height={height}
          transform={`translate(${Math.round(x * 10) / 10} ${Math.round(y * 10) / 10})`}
          onClick={() => selectRef.current(f.id)}
        >
          <title>{title}</title>
          <polygon className="atlas-prism__floor" points={diamond(0)} />
          {segs.length > 0 && (
            <g className={animate ? 'atlas-prism__col atlas-grow' : 'atlas-prism__col'} data-testid="prism-col">
              {segs.map((s) => {
                const faces = sideFaces(s.from, s.to);
                const color = DOMAIN[s.key].color;
                return (
                  <g key={s.key} data-testid="prism-seg" data-domain={s.key} data-count={s.count} data-from={s.from} data-to={s.to}>
                    <polygon points={faces.left} fill={color} />
                    <polygon points={faces.left} className="atlas-shade-left" />
                    <polygon points={faces.right} fill={color} />
                    <polygon points={faces.right} className="atlas-shade-right" />
                  </g>
                );
              })}
              <polygon points={diamond(height)} fill={DOMAIN[top.key].color} />
              <polygon points={diamond(height)} className="atlas-shade-top" />
            </g>
          )}
          {selected && (
            <>
              <polygon className="atlas-prism__ring" points={silhouette(height)} />
              <text className="atlas-prism__label" x={0} y={-height - PRISM_B - 6} textAnchor="middle">
                {shownTotal(f.open, shown).toLocaleString('en-US')}
              </text>
            </>
          )}
        </g>
      );
    });
  }, [frame, shown, k, selectedId, animate, growKey]);

  return (
    <svg
      className="atlas-prisms"
      data-testid="atlas-prisms"
      data-zooming={zooming ? 'true' : undefined}
      width={frame?.w ?? 0}
      height={frame?.h ?? 0}
      aria-hidden="true"
      focusable="false"
    >
      <g ref={rootRef}>{columns}</g>
    </svg>
  );
}
